import React, { useState } from 'react';
import { API_BASE_URL } from '../constants/api';

type Member = {
  id: number;
  name: string;
  email: string;
  phoneNumber: string;
  business_Or_Employer: string;
  applied_On: string;
  status: string;
};

interface EditMemberModalProps {
  member: Member | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (updatedMember: Member) => void;
}

const EditMemberModal: React.FC<EditMemberModalProps> = ({ member, isOpen, onClose, onSave }) => {
  const [editMember, setEditMember] = useState<Member | null>(member);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [nameError, setNameError] = useState('');

  // Update local state when member prop changes
  React.useEffect(() => {
    setEditMember(member);
  }, [member]);

  if (!isOpen || !editMember) return null;

  // Name validation function
  const validateName = (name: string): boolean => {
    // Empty names are invalid
    if (!name || name.trim() === '') {
      return false;
    }
    return true;
  };

  // Email validation function
  const validateEmail = (email: string): boolean => {
    // Empty emails are invalid
    if (!email || email.trim() === '') {
      return false;
    }
    // Basic email regex pattern
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // US phone number validation function
  const validatePhoneNumber = (phone: string): boolean => {
    // Empty phone numbers are invalid
    if (!phone || phone.trim() === '') {
      return false;
    }
    // Remove all non-digit characters
    const digitsOnly = phone.replace(/\D/g, '');
    // US phone numbers should have 10 digits (or 11 with country code)
    return digitsOnly.length === 10 || digitsOnly.length === 11;
  };

  // Format phone number as user types
  const formatPhoneNumber = (value: string): string => {
    // Remove all non-digit characters
    const digitsOnly = value.replace(/\D/g, '');
    
    // Format as (XXX) XXX-XXXX
    if (digitsOnly.length <= 3) {
      return digitsOnly;
    } else if (digitsOnly.length <= 6) {
      return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3)}`;
    } else {
      return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6, 10)}`;
    }
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEditMember(m => m ? { ...m, name: value } : m);
    
    // Validate name
    if (!validateName(value)) {
      setNameError('Please enter a name');
    } else {
      setNameError('');
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEditMember(m => m ? { ...m, email: value } : m);
    
    // Validate email
    if (!validateEmail(value)) {
      setEmailError('Please enter a valid email address');
    } else {
      setEmailError('');
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const formattedValue = formatPhoneNumber(value);
    
    setEditMember(m => m ? { ...m, phoneNumber: formattedValue } : m);
    
    // Validate phone number
    if (!validatePhoneNumber(formattedValue)) {
      setPhoneError('Please enter a valid US phone number (10 digits)');
    } else {
      setPhoneError('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setPhoneError('');
    setEmailError('');
    setNameError('');

    // Validate name before submission
    if (!validateName(editMember.name)) {
      setNameError('Please enter a name');
      setLoading(false);
      return;
    }

    // Validate email before submission
    if (!validateEmail(editMember.email)) {
      setEmailError('Please enter a valid email address');
      setLoading(false);
      return;
    }

    // Validate phone number before submission
    if (!validatePhoneNumber(editMember.phoneNumber)) {
      setPhoneError('Please enter a valid US phone number (10 digits)');
      setLoading(false);
      return;
    }

    try {
      // TODO: Implement the actual API call to update the member
      const response = await fetch(`${API_BASE_URL}/potentialnewmembers/${editMember.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editMember)
      });

      if (!response.ok) {
        throw new Error('Failed to update member');
      }

      // const updatedMember = await response.json();
      onSave(editMember);
      onClose();
    } catch (err) {
      console.error(err);
      setError('Error updating member.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dashboard-modal-backdrop">
      <div className="dashboard-modal">
        <h2>Edit Member</h2>
        {error && <div className="dashboard-modal-error">{error}</div>}
        <form className="dashboard-modal-form" onSubmit={handleSubmit}>
          <label>
            Name
            <input 
              type="text" 
              value={editMember.name || ''} 
              onChange={handleNameChange}
              placeholder="Enter member name"
              required 
            />
            {nameError && <div style={{ color: '#d32f2f', fontSize: '0.8rem', marginTop: '4px' }}>{nameError}</div>}
          </label>
          <label>
            Email
            <input 
              type="email" 
              value={editMember.email || ''} 
              onChange={handleEmailChange}
              placeholder="example@email.com"
              required 
            />
            {emailError && <div style={{ color: '#d32f2f', fontSize: '0.8rem', marginTop: '4px' }}>{emailError}</div>}
          </label>
          <label>
            Phone
            <input 
              type="tel" 
              value={editMember.phoneNumber || ''} 
              onChange={handlePhoneChange}
              placeholder="(555) 123-4567"
              maxLength={14}
            />
            {phoneError && <div style={{ color: '#d32f2f', fontSize: '0.8rem', marginTop: '4px' }}>{phoneError}</div>}
          </label>
          <label>
            Business/Employer
            <input 
              type="text" 
              value={editMember.business_Or_Employer || ''} 
              onChange={e => setEditMember(m => m ? { ...m, business_Or_Employer: e.target.value } : m)} 
              required 
            />
          </label>
          <label>
            Status
            <select 
              value={editMember.status || 'Pending'} 
              onChange={e => setEditMember(m => m ? { ...m, status: e.target.value } : m)} 
              required
            >
              <option value="Pending">Pending</option>
              <option value="Approved">Approved</option>
              <option value="Rejected">Rejected</option>
            </select>
          </label>
          <div className="dashboard-modal-actions">
            <button 
              type="button" 
              className="dashboard-modal-cancel" 
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="dashboard-modal-save"
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditMemberModal; 